import { useNavigate } from "react-router-dom";
import { Workforce, isEndingSoon, getDaysUntilEnd, calculateMargin } from "@/types/workforce";
import { Building2, Calendar, MapPin, Loader2, AlertTriangle, TrendingUp, Eye, Pencil } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface WorkforceKanbanCardProps {
	workforce: Workforce;
	isUpdating?: boolean;
	onEdit?: (workforceId: string) => void;
}

const formatDate = (dateStr: string | null | undefined) => {
	if (!dateStr) return null;
	try {
		return format(new Date(dateStr), 'MMM d');
	} catch {
		return dateStr;
	}
};

export const WorkforceKanbanCard = ({ workforce, isUpdating, onEdit }: WorkforceKanbanCardProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const candidateName = workforce.candidate?.name || 'Unknown';
	const companyName = workforce.job?.company?.name || workforce.external_company_name || 'Unknown';
	const jobTitle = workforce.job?.title || workforce.external_job_title || 'Unknown Role';

	// Calculate if ending soon (within 30 days) and days remaining
	const endingSoon = workforce.status === 'active' && isEndingSoon(workforce.expected_end_date || workforce.end_date, 30);
	const daysUntilEnd = getDaysUntilEnd(workforce.expected_end_date || workforce.end_date);
	const isUrgent = daysUntilEnd !== null && daysUntilEnd <= 7;

	// Calculate margin
	const dailyMargin = calculateMargin(workforce.purchase_price_daily, workforce.selling_price_daily);
	const hourlyMargin = calculateMargin(workforce.purchase_price_hourly, workforce.selling_price_hourly);
	const margin = dailyMargin ?? hourlyMargin;

	const handleDragStart = (e: React.DragEvent) => {
		e.dataTransfer.setData('workforceId', workforce.id);
		e.dataTransfer.effectAllowed = 'move';
	};

	return (
		<div
			draggable={!isUpdating}
			onDragStart={handleDragStart}
			className={cn(
				"bg-white/95 dark:bg-black/80 backdrop-blur-sm rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm p-3 cursor-move transition-all duration-300 relative overflow-hidden",
				"hover:shadow-lg hover:scale-[1.02]",
				isUpdating && "opacity-50 cursor-not-allowed",
				"active:cursor-grabbing"
			)}
			style={{
				boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
			}}
		>
			{isUpdating && (
				<div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-sm z-10">
					<Loader2 className="h-5 w-5 animate-spin" />
				</div>
			)}

			{/* Ending Soon Indicator */}
			{endingSoon && (
				<div className={cn(
					"absolute top-0 right-0 px-1.5 py-0.5 text-[10px] font-medium rounded-bl bg-muted",
					isUrgent && "font-semibold"
				)}>
					<div className="flex items-center gap-0.5">
						<AlertTriangle className="h-2.5 w-2.5" />
						{daysUntilEnd}d
					</div>
				</div>
			)}

			{/* Consultant Name - clickable to open candidate */}
			<div className="flex items-center gap-2 mb-2">
				<Avatar className="h-6 w-6 flex-shrink-0">
					<AvatarFallback className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
						{candidateName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
					</AvatarFallback>
				</Avatar>
				{workforce.candidate_id ? (
					<span
						className="font-medium text-sm truncate cursor-pointer hover:text-primary hover:underline transition-colors"
						onClick={(e) => { e.stopPropagation(); navigate(`/candidates/${workforce.candidate_id}`); }}
					>
						{candidateName}
					</span>
				) : (
					<span className="font-medium text-sm truncate">{candidateName}</span>
				)}
			</div>

			{/* Company */}
			<div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
				<Building2 className="h-3 w-3 flex-shrink-0" />
				<span className="truncate">{companyName}</span>
			</div>

			{/* Role */}
			<div className="text-xs text-muted-foreground mb-2 truncate">
				{jobTitle}
			</div>

			{/* Margin Badge */}
			{margin !== null && (
				<div className="mb-2">
					<Badge variant="secondary" className="text-[10px] px-1.5 py-0">
						<TrendingUp className="h-2.5 w-2.5 mr-0.5 inline" />
						{margin.toFixed(0)}%
					</Badge>
				</div>
			)}

			{/* Footer Info */}
			<div className="flex items-center justify-between text-xs text-muted-foreground">
				<div className="flex items-center gap-1">
					<Calendar className="h-3 w-3" />
					<span>{formatDate(workforce.start_date)}</span>
					{workforce.expected_end_date && (
						<span>- {formatDate(workforce.expected_end_date)}</span>
					)}
				</div>
				<div className="flex items-center gap-1">
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className="p-1 rounded hover:bg-muted transition-colors"
								onClick={(e) => { e.stopPropagation(); navigate(`/workforce/${workforce.id}`); }}
							>
								<Eye className="h-3.5 w-3.5" />
							</button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("common.view", "View")}</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className="p-1 rounded hover:bg-muted transition-colors"
								onClick={(e) => { e.stopPropagation(); onEdit?.(workforce.id); }}
							>
								<Pencil className="h-3.5 w-3.5" />
							</button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("common.edit", "Edit")}</TooltipContent>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};
